import { useState, useCallback, useMemo, useEffect } from 'react';

// ─── Types ──────────────────────────────────────────────────

export interface NavItemDef {
  id: string;
  defaultGroup: 'primary' | 'secondary' | 'more';
  defaultOrder: number;
}

interface StoredConfig {
  version: number;
  visibleIds: string[];
  updatedAt: string;
}

/** Bump when new default-visible nav items ship so legacy localStorage picks them up once. */
const STORAGE_VERSION = 5;
const PINNED_ITEMS = ['dashboard'];

function storageKey(userId: string) {
  return `navigation-config-${userId}`;
}

// ─── Default visible IDs (primary + secondary) ─────────────

export function getDefaultVisibleIds(allItems: NavItemDef[]): string[] {
  return allItems
    .filter(i => i.defaultGroup === 'primary' || i.defaultGroup === 'secondary')
    .sort((a, b) => {
      const groupOrder = { primary: 0, secondary: 1, more: 2 };
      const gDiff = groupOrder[a.defaultGroup] - groupOrder[b.defaultGroup];
      return gDiff !== 0 ? gDiff : a.defaultOrder - b.defaultOrder;
    })
    .map(i => i.id);
}

// ─── Read / Write localStorage ──────────────────────────────

function readConfig(userId: string): StoredConfig | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const v = typeof parsed?.version === 'number' ? parsed.version : 1;
    if (Array.isArray(parsed?.visibleIds) && v >= 1 && v <= STORAGE_VERSION) {
      return { visibleIds: parsed.visibleIds, version: v, updatedAt: parsed.updatedAt ?? '' };
    }
    return null;
  } catch {
    return null;
  }
}

function writeConfig(userId: string, visibleIds: string[], version: number = STORAGE_VERSION) {
  const data: StoredConfig = {
    version,
    visibleIds,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(storageKey(userId), JSON.stringify(data));
}

/** Append any default-visible ids missing from a legacy saved list (one-time per STORAGE_VERSION bump). */
function mergeNewDefaultVisibleIds(
  cleaned: string[],
  defaultVisibleIds: string[],
  allIds: Set<string>
): string[] {
  const next = [...cleaned];
  for (const id of defaultVisibleIds) {
    if (allIds.has(id) && !next.includes(id)) {
      next.push(id);
    }
  }
  return next;
}

// ─── Hook ───────────────────────────────────────────────────

/**
 * Manages per-user navigation layout with localStorage persistence.
 *
 * @param userId        Current user ID (undefined before auth)
 * @param allItems      Flat list of every nav item with default group/order
 * @param canReadItem   Permission check – receives item id, returns boolean
 */
export function useNavigationConfig(
  userId: string | undefined,
  allItems: NavItemDef[],
  canReadItem: (id: string) => boolean,
) {
  const defaultVisibleIds = useMemo(() => getDefaultVisibleIds(allItems), [allItems]);
  const allIds = useMemo(() => new Set(allItems.map(i => i.id)), [allItems]);

  const [visibleIds, setVisibleIds] = useState<string[]>(() => {
    if (!userId) return defaultVisibleIds;
    const stored = readConfig(userId);
    if (!stored) return defaultVisibleIds;
    let cleaned = stored.visibleIds.filter(id => allIds.has(id));
    for (const pin of PINNED_ITEMS) {
      if (allIds.has(pin) && !cleaned.includes(pin)) {
        cleaned.unshift(pin);
      }
    }
    if (stored.version < STORAGE_VERSION) {
      cleaned = mergeNewDefaultVisibleIds(cleaned, defaultVisibleIds, allIds);
      writeConfig(userId, cleaned);
    }
    return cleaned;
  });

  // Re-read when userId changes (login / switch)
  useEffect(() => {
    if (!userId) {
      setVisibleIds(defaultVisibleIds);
      return;
    }
    const stored = readConfig(userId);
    if (!stored) {
      setVisibleIds(defaultVisibleIds);
      return;
    }
    let cleaned = stored.visibleIds.filter(id => allIds.has(id));
    for (const pin of PINNED_ITEMS) {
      if (allIds.has(pin) && !cleaned.includes(pin)) {
        cleaned.unshift(pin);
      }
    }
    if (stored.version < STORAGE_VERSION) {
      cleaned = mergeNewDefaultVisibleIds(cleaned, defaultVisibleIds, allIds);
      writeConfig(userId, cleaned);
    }
    setVisibleIds(cleaned);
  }, [userId, allIds, defaultVisibleIds]);

  // Apply permission filtering on top of user preference
  const permittedVisibleIds = useMemo(
    () => visibleIds.filter(id => canReadItem(id)),
    [visibleIds, canReadItem],
  );

  const availableIds = useMemo(() => {
    const visibleSet = new Set(visibleIds);
    return allItems
      .filter(i => !visibleSet.has(i.id) && canReadItem(i.id))
      .map(i => i.id);
  }, [allItems, visibleIds, canReadItem]);

  const updateConfig = useCallback((newVisibleIds: string[]) => {
    // Enforce pinned items at the start
    const ids = [...newVisibleIds];
    for (const pin of PINNED_ITEMS) {
      if (allIds.has(pin) && !ids.includes(pin)) {
        ids.unshift(pin);
      }
    }
    setVisibleIds(ids);
    if (userId) writeConfig(userId, ids);
  }, [userId, allIds]);

  const resetToDefault = useCallback(() => {
    setVisibleIds(defaultVisibleIds);
    if (userId) writeConfig(userId, defaultVisibleIds);
  }, [userId, defaultVisibleIds]);

  return {
    visibleIds: permittedVisibleIds,
    rawVisibleIds: visibleIds,
    availableIds,
    updateConfig,
    resetToDefault,
    isPinned: (id: string) => PINNED_ITEMS.includes(id),
  };
}
